/**
 * Round 6 additions to the distribution mock: a test run's targets (each test copy goes to every
 * target instead of to its recipient), the message each recipient of a copy is sent, and the brand
 * a copy is drawn with. Split from distributionsApi.mock.ts to keep both files readable.
 */
import { MockDomainError } from "../mockDomainError";
import { DISTRIBUTION_CHANNELS } from "./distributionChannels";
import { andList, formatShortDate, pluralize } from "./distributionFormat";
import { canonicalValue } from "./distributionRecipientRules";
import { firstNameFor, renderTokens } from "./distributionTokens";
import { countsOf, type MockRunRecord } from "./distributionsMockRuns";
import {
  MAX_TEST_TARGETS,
  type AudienceApi,
  type CopyMessageApi,
  type DeliveryApi,
  type DistributionApi,
  type RecipientInputApi,
  type RowErrorDetailApi,
  type SliceBrandApi,
  type TestTargetApi,
} from "./distributions.types";

export interface MockTestTargetContext {
  slackConnected: boolean;
  audience: AudienceApi;
  /** The organisation's own email domains, lower-cased: from data, as api-service computes them. */
  ownDomains: readonly string[];
}

/** "name@company.com" → "company.com". */
export const emailDomain = (address: string): string => address.trim().toLowerCase().split("@").pop() ?? "";

/** api-service's 422 for refused targets: `error.code = "validation"`, one detail per target. */
const refuse = (details: RowErrorDetailApi[]): MockDomainError =>
  new MockDomainError({ status: 422, code: "validation", message: details[0].message, details });

const detail = (index: number, field: string, code: string, message: string): RowErrorDetailApi => ({ position: index + 1, field, code, message });

export const outsideAddressMessage = (address: string): string =>
  `${address} is outside your organisation, so a copy for people inside it can’t go there. Send the test to an address in your organisation, or choose “Outside the organisation” in step 1.`;

export const SLACK_NOT_CONNECTED_MESSAGE =
  "Connect Slack first. Slack isn’t connected for your organisation, so a test can’t post to a channel.";

/**
 * The 422 api-service answers for a test run's targets, or null when it would accept them: each
 * bad target (`position` = its 1-based index), each Slack target without a connection, each
 * outside address for an inside audience.
 */
export const mockTestTargetsError = (targets: readonly TestTargetApi[], ctx: MockTestTargetContext): MockDomainError | null => {
  if (targets.length > MAX_TEST_TARGETS) {
    return refuse([{ position: 0, field: "test_targets", code: "too_long", message: `A test goes to ${MAX_TEST_TARGETS} places at most. Remove some, or send a second test.` }]);
  }
  const invalid = targets.flatMap((target, i) => {
    const problem = DISTRIBUTION_CHANNELS[target.channel].validate(target.address);
    return problem ? [detail(i, "test_targets", "value_error", problem)] : [];
  });
  if (invalid.length) return refuse(invalid);
  const slack = ctx.slackConnected
    ? []
    : targets.flatMap((target, i) => (target.channel === "slack" ? [detail(i, "test_targets.channel", "slack_not_connected", SLACK_NOT_CONNECTED_MESSAGE)] : []));
  if (slack.length) return refuse(slack);
  const outside =
    ctx.audience === "inside"
      ? targets.flatMap((target, i) =>
          target.channel === "email" && !ctx.ownDomains.includes(emailDomain(target.address))
            ? [detail(i, "test_targets.address", "outside_address", outsideAddressMessage(target.address))]
            : [],
        )
      : [];
  return outside.length ? refuse(outside) : null;
};

/** A run other than a test that names targets. */
export const mockTestOnlyError = (): MockDomainError =>
  refuse([{ position: 0, field: "test_targets", code: "test_only", message: "Only a test run goes to test targets." }]);

/** The same channel and address in any case is sent once. */
export const distinctTargets = (targets: readonly TestTargetApi[]): TestTargetApi[] =>
  targets.filter(
    (target, i) => targets.findIndex((other) => other.channel === target.channel && other.address.toLowerCase() === target.address.toLowerCase()) === i,
  );

const notInChannel = (address: string): string =>
  `MagicBI isn’t in ${address} yet. In Slack, open the channel and type /invite @MagicBI, then send the test again.`;

/**
 * A test run whose copies go to every target instead of to their recipients: one delivery per
 * copy and target. A copy that was held or skipped goes nowhere. A Slack channel without the app
 * fails, as a real post would.
 */
export const withTestTargets = (
  record: MockRunRecord,
  targets: readonly TestTargetApi[],
  slackChannelsWithoutApp: ReadonlySet<string>,
): MockRunRecord => {
  const deliveries: DeliveryApi[] = record.detail.deliveries.flatMap((delivery) =>
    targets.map((target, i): DeliveryApi => {
      const base: DeliveryApi = {
        ...delivery,
        id: `${delivery.id}-t${i + 1}`,
        recipient: { ...delivery.recipient, channel: target.channel, address: target.address, cc: [] },
      };
      if (delivery.status !== "sent") return base;
      if (target.channel === "slack" && slackChannelsWithoutApp.has(target.address.trim().toLowerCase())) {
        return { ...base, status: "failed", sent_at: null, error: notInChannel(target.address) };
      }
      return base;
    }),
  );
  const counts = countsOf(deliveries, record.detail.slices.length);
  const failed = deliveries.filter((d) => d.status === "failed").map((d) => d.recipient.address);
  return {
    ...record,
    detail: {
      ...record.detail,
      deliveries,
      run: {
        ...record.detail.run,
        counts,
        status: counts.failed ? (counts.sent ? "partly_failed" : "failed") : "succeeded",
        error: failed.length ? `${pluralize(failed.length, "copy", "copies")} didn’t arrive: ${andList(failed)}.` : null,
      },
    },
  };
};

export interface MockMessageContext {
  storyTitle: string;
  /** Canonical values of the split field. */
  values: readonly string[];
  /** The AI summary of a value's numbers; null when off. */
  summaryFor: (value: string) => string | null;
  now: number;
}

const tokenValues = (dist: DistributionApi, row: RecipientInputApi, value: string, ctx: MockMessageContext, company: string | null) => ({
  ...Object.fromEntries(Object.entries(row.extra ?? {}).map(([key, text]) => [key.toLowerCase(), text])),
  name: firstNameFor(row.name, row.channel),
  first_name: firstNameFor(row.name, row.channel),
  email: row.channel === "email" ? row.address : "",
  value,
  field: dist.split?.label ?? "",
  story: ctx.storyTitle,
  date: formatShortDate(dist.next_run_at ?? new Date(ctx.now).toISOString(), dist.schedule?.timezone ?? "UTC"),
  sender: dist.created_by?.name ?? "",
  company: company ?? "",
});

const withSummary = (text: string, summary: string | null): string => (summary ? (text ? `${text}\n\n${summary}` : summary) : text);

/** What one recipient is sent (magic-core `CopyMessage`): the message with their tokens filled in. */
export const mockCopyMessage = (dist: DistributionApi, row: RecipientInputApi, ctx: MockMessageContext): CopyMessageApi => {
  const value = canonicalValue(row.value, ctx.values) ?? row.value;
  const { content } = dist;
  const brand = mockSliceBrand(dist, row);
  const values = tokenValues(dist, row, value, ctx, brand.name);
  const summary = content.include_ai_summary ? ctx.summaryFor(value) : null;
  const message = (template: string) => (content.include.message ? renderTokens(template, values) : "");
  const isSlack = row.channel === "slack";
  return {
    position: row.position,
    channel: row.channel,
    subject: isSlack ? null : renderTokens(content.message.email_subject, values),
    body: isSlack ? null : withSummary(message(content.message.email_body), summary),
    slack_text: isSlack ? withSummary(message(content.message.slack_text), summary) : null,
  };
};

/** The brand a row's copy is drawn with: its own brand over the distribution's, field by field. */
export const mockSliceBrand = (dist: DistributionApi, row: RecipientInputApi | undefined): SliceBrandApi => {
  const own = row?.brand ?? null;
  const fallback = dist.content.branding;
  const ownName = own?.name?.trim() || null;
  return {
    name: ownName ?? (fallback.name?.trim() || null),
    accent: own?.accent ?? fallback.accent ?? null,
    mark: own?.mark ?? (ownName ? null : fallback.mark ?? null),
    palette: [],
    // A row that names its own brand never borrows the default logo, which is another organisation's.
    logo_url: own?.logo_url ?? (ownName ? null : fallback.logo_url ?? null),
  };
};

/** Each slice's messages (one per recipient of the copy) and brand, keyed by slice id. */
export const mockSliceExtras = (
  record: MockRunRecord,
  dist: DistributionApi,
  rows: readonly RecipientInputApi[],
  ctx: MockMessageContext,
): Pick<MockRunRecord, "messages" | "brands"> => {
  const messages: Record<string, CopyMessageApi[]> = {};
  const brands: Record<string, SliceBrandApi | null> = {};
  record.detail.slices.forEach((slice) => {
    const sliceRows = rows.filter((row) => (canonicalValue(row.value, ctx.values) ?? row.value) === slice.value);
    messages[slice.id] = sliceRows.map((row) => mockCopyMessage(dist, row, ctx));
    brands[slice.id] = mockSliceBrand(dist, sliceRows[0]);
  });
  return { messages, brands };
};

